import { IClient } from '@api/models';
import { sendClientNotification } from '@api/services/notification.service';
import useDialog from '@hooks/use-dialog';
import { Button, HelpText } from '@hyperflake/react-ui-library';
import { Label } from '@library/forms';
import { ErrorMessage, Formik, FormikHelpers } from 'formik';
import ClientConfirmationDrawer from 'modules/notification/components/ClientConfirmationDrawer/ClientConfirmationDrawer';
import { FormikInput } from 'modules/shared/features/formik';
import FormikTextarea from 'modules/shared/features/formik/components/FormikTextarea/FormikTextarea';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

const notificationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    text: Yup.string().required('Text is required'),
});

const initialValues = { title: '', text: '' };

interface NotificationFormProps {
    selectedIds: string[];
    clients: IClient[];
    resetState: () => void;
}

const ClientNotificationForm = ({ selectedIds, clients, resetState }: NotificationFormProps) => {
    const confirmationDrawerDialog = useDialog<IClient[]>();
    const [formValues, setFormValues] = useState<{ title: string; text: string } | null>(null);
    const [isSending, setIsSending] = useState(false);
    const resetFormRef = useRef<(() => void) | null>(null);

    const handleSubmit = (
        values: { title: string; text: string },
        { resetForm }: FormikHelpers<{ title: string; text: string }>
    ) => {
        resetFormRef.current = resetForm;
        setFormValues(values);
        confirmationDrawerDialog.show(clients.filter((client) => selectedIds.includes(client._id)));
    };

    const handleSendNotification = async () => {
        try {
            setIsSending(true);
            await sendClientNotification({
                clientIds: selectedIds,
                title: formValues.title,
                message: formValues.text,
            });
            toast.success('Notification sent');
            confirmationDrawerDialog.hide();
            resetFormRef.current?.();
            resetState();
        } catch (err: any) {
            console.error(err);
            toast.error(err?.data?.message || 'Something went wrong.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <>
            <Formik initialValues={initialValues} validationSchema={notificationSchema} onSubmit={handleSubmit}>
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <h2 className="font-semibold text-base">Create Notification</h2>

                        <div className="mt-4">
                            <Label>Notification Title</Label>
                            <FormikInput name="title" placeholder="Enter notification title" />
                            <HelpText variant="error">
                                <ErrorMessage name="title" />
                            </HelpText>
                        </div>

                        <div className="mt-4">
                            <Label>Notification Text</Label>
                            <FormikTextarea rows={4} name="text" placeholder="Enter notification text" />
                            <HelpText variant="error">
                                <ErrorMessage name="text" />
                            </HelpText>
                        </div>

                        <div className="mt-4">
                            <Button type="submit" color="primary" disabled={selectedIds.length === 0}>
                                Send Notification
                            </Button>
                        </div>
                    </form>
                )}
            </Formik>

            <ClientConfirmationDrawer
                open={confirmationDrawerDialog.isOpen}
                onClose={confirmationDrawerDialog.hide}
                selectedClient={confirmationDrawerDialog.data}
                isSending={isSending}
                onSend={handleSendNotification}
                title={formValues?.title ?? ''}
                text={formValues?.text ?? ''}
            />
        </>
    );
};

export default ClientNotificationForm;
